import React, { useEffect, useState } from 'react';
import { useAuthContext } from '../auth-config';
import type { Chapter } from '../auth-config/types';
import { authService } from '../auth-config/auth-service';
import toast from 'react-hot-toast';
import { 
  FiAward, 
  FiBookOpen, 
  FiCheckCircle, 
  FiPlay, 
  FiPieChart, 
  FiChevronDown, 
  FiChevronUp,
  FiClock
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { encryptId } from '../utils/crypto';

const ProgressPage: React.FC = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [completedVideoIds, setCompletedVideoIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSubjectCode, setActiveSubjectCode] = useState<string>('');

  useEffect(() => {
    const fetchProgressData = async () => {
      try {
        const token = await authService.getAccessToken();
        if (!token) return;

        // 1. Fetch all curriculum chapters and topics for the student
        const chaptersRes = await fetch('/api/student/chapters', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!chaptersRes.ok) throw new Error('Failed to load curriculum course outline.');
        const chaptersData: Chapter[] = await chaptersRes.json();
        setChapters(chaptersData);

        // 2. Fetch completed video list
        const completedRes = await fetch('/api/student/videos/completed', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!completedRes.ok) throw new Error('Failed to load completed lessons.');
        const completedData: number[] = await completedRes.json();
        setCompletedVideoIds(completedData);

        // Auto-select the first subject
        const uniqueSubjects = Array.from(new Set(chaptersData.map(c => c.subjectCode)));
        if (uniqueSubjects.length > 0) {
          setActiveSubjectCode(uniqueSubjects[0]);
        }
      } catch (err: any) {
        console.error(err);
        toast.error(err.message || 'An error occurred while loading progress data.');
      } finally {
        setLoading(false);
      }
    };

    fetchProgressData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // Helper to format subject codes to friendly titles
  const getSubjectName = (code: string) => {
    if (!code || code.trim() === '') return 'General';
    const prefix = code.split('-')[0].toUpperCase();
    switch (prefix) {
      case 'MATH': return 'Mathematics';
      case 'SCI': return 'Science';
      case 'HIN': return 'Hindi';
      case 'GUJ': return 'Gujarati';
      case 'ENG': return 'English';
      case 'SST': return 'Social Studies';
      default: return prefix;
    }
  };

  // Group chapters by Subject
  const subjectsMap: Record<string, Chapter[]> = {};
  chapters.forEach(c => {
    if (!subjectsMap[c.subjectCode]) {
      subjectsMap[c.subjectCode] = [];
    }
    subjectsMap[c.subjectCode].push(c);
  });

  // Calculate totals
  let totalTopicsCount = 0;
  let completedTopicsCount = 0;
  let totalDurationSeconds = 0;

  chapters.forEach(c => {
    c.topics.forEach(t => {
      totalTopicsCount++;
      totalDurationSeconds += t.duration;
      if (completedVideoIds.includes(t.videoId)) {
        completedTopicsCount++;
      }
    });
  });

  const overallProgressPercentage = totalTopicsCount > 0 
    ? Math.round((completedTopicsCount / totalTopicsCount) * 100) 
    : 0;

  const formatHours = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.round((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins} mins`;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-8 animate-fadeIn text-slate-800">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-150 pb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900">Academic Progress</h1>
          <p className="text-sm text-gray-500 font-semibold mt-1">
            Track your course completions, watch times, and view interactive curriculum checklists.
          </p>
        </div>
        <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-2xl border border-purple-100">
          <span className="w-10 h-10 rounded-xl bg-purple-600 text-white flex items-center justify-center font-bold">
            🏆
          </span>
          <div>
            <span className="text-[10px] font-bold text-purple-600 uppercase tracking-wider block">Level Badge</span>
            <span className="text-xs font-bold text-purple-900">
              {overallProgressPercentage >= 100 ? 'Course Master' : overallProgressPercentage >= 50 ? 'Rising Star' : 'Active Learner'}
            </span>
          </div>
        </div>
      </div>

      {/* Highlights Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Progress Circle Card */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-200/50 flex items-center gap-6">
          <div className="relative w-20 h-20 flex items-center justify-center">
            <svg className="w-20 h-20 transform -rotate-90">
              <circle cx="40" cy="40" r="34" fill="none" stroke="#f3f4f6" strokeWidth="6" />
              <circle 
                cx="40" 
                cy="40" 
                r="34" 
                fill="none" 
                stroke="#6b21a8" 
                strokeWidth="6" 
                strokeDasharray="213.6" 
                strokeDashoffset={213.6 - (213.6 * overallProgressPercentage) / 100} 
                className="transition-all duration-1000 ease-out"
              />
            </svg>
            <span className="absolute text-sm font-extrabold text-purple-950">{overallProgressPercentage}%</span>
          </div>
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Overall Progress</h4>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">{completedTopicsCount} / {totalTopicsCount}</p>
            <span className="text-xs font-semibold text-gray-500">lessons completed</span>
          </div>
        </div>

        {/* Total Watch Time Card */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-200/50 flex items-center gap-5">
          <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-600 flex items-center justify-center">
            <FiClock size={20} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Course Duration</h4>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">{formatHours(totalDurationSeconds)}</p>
            <span className="text-xs font-semibold text-gray-500">total syllabus length</span>
          </div>
        </div>

        {/* Completed Lectures Badge Card */}
        <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-xl shadow-gray-200/50 flex items-center gap-5">
          <div className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-100 text-emerald-600 flex items-center justify-center">
            <FiAward size={20} />
          </div>
          <div>
            <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Certificates</h4>
            <p className="text-2xl font-extrabold text-gray-900 mt-1">
              {overallProgressPercentage >= 100 ? '1 Earned' : '0 Earned'}
            </p>
            <span className="text-xs font-semibold text-gray-500">unlocked at 100% completion</span>
          </div>
        </div>
      </div>

      {/* Curriculum breakdown sections */}
      <div className="space-y-6">
        <div className="flex items-center justify-between border-b border-gray-100 pb-2">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FiPieChart className="text-purple-600" /> Subject-wise Details
          </h2>
        </div>

        {/* Subject Tabs */}
        <div className="flex flex-wrap gap-2.5">
          {Object.entries(subjectsMap).map(([subjectCode, subjChapters]) => {
            let subjTotal = 0;
            let subjCompleted = 0;
            subjChapters.forEach(c => {
              c.topics.forEach(t => {
                subjTotal++;
                if (completedVideoIds.includes(t.videoId)) {
                  subjCompleted++;
                }
              });
            });
            const subjProgress = subjTotal > 0 ? Math.round((subjCompleted / subjTotal) * 100) : 0;
            const isActive = activeSubjectCode === subjectCode;

            return (
              <button
                key={subjectCode}
                onClick={() => setActiveSubjectCode(subjectCode)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border font-bold text-xs transition-all duration-200 ${
                  isActive 
                    ? 'bg-purple-600 border-purple-600 text-white shadow-md shadow-purple-500/20' 
                    : 'bg-white border-gray-200 text-slate-600 hover:bg-gray-50 hover:border-gray-300'
                }`}
              >
                <FiBookOpen size={14} />
                <span>{getSubjectName(subjectCode)}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-extrabold ${
                  isActive ? 'bg-purple-700/50 text-white' : 'bg-gray-150 text-slate-500'
                }`}>
                  {subjProgress}%
                </span>
              </button>
            );
          })}
        </div>

        {/* Active Subject Content */}
        {subjectsMap[activeSubjectCode] !== undefined && (() => {
          const subjChapters = subjectsMap[activeSubjectCode];
          let subjTotal = 0;
          let subjCompleted = 0;
          subjChapters.forEach(c => {
            c.topics.forEach(t => {
              subjTotal++;
              if (completedVideoIds.includes(t.videoId)) {
                subjCompleted++;
              }
            });
          });
          const subjProgress = subjTotal > 0 ? Math.round((subjCompleted / subjTotal) * 100) : 0;

          return (
            <div className="space-y-6">
              {/* Subject Overview Card */}
              <div className="bg-gradient-to-r from-purple-900 to-indigo-950 rounded-3xl p-6 text-white shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-2">
                  <span className="text-[10px] font-bold tracking-widest uppercase bg-purple-500/30 border border-purple-500/20 px-3 py-1 rounded-full text-purple-300">
                    Selected Subject
                  </span>
                  <h2 className="text-2xl font-black">{getSubjectName(activeSubjectCode)}</h2>
                  <p className="text-xs text-purple-200 font-medium">
                    {subjChapters.length} Chapters • {subjTotal} Lessons total in syllabus
                  </p>
                </div>

                <div className="flex flex-col items-start md:items-end gap-2">
                  <span className="text-xs font-bold text-purple-200">Subject Completion</span>
                  <div className="flex items-center gap-3 w-60">
                    <div className="flex-1 bg-purple-950/60 rounded-full h-2.5 overflow-hidden border border-purple-700/30">
                      <div 
                        className="bg-gradient-to-r from-purple-400 to-indigo-400 h-full rounded-full transition-all duration-1000"
                        style={{ width: `${subjProgress}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-black text-white">{subjProgress}%</span>
                  </div>
                </div>
              </div>

              {/* Chapters & Topics List */}
              <div className="space-y-6">
                {subjChapters.map(chap => (
                  <div key={chap.chapterId} className="bg-white border border-gray-100 rounded-3xl p-6 shadow-md shadow-gray-100/50 space-y-4">
                    <div className="border-b border-gray-100 pb-3 flex items-center justify-between">
                      <h3 className="text-xs font-extrabold text-slate-800 uppercase tracking-wider">
                        Chapter {chap.chapterNumber}: {chap.chapterName}
                      </h3>
                      <span className="text-xs text-gray-400 font-bold">{chap.topics.length} Lectures</span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {chap.topics.map(topic => {
                        const isCompleted = completedVideoIds.includes(topic.videoId);
                        return (
                          <div 
                            key={topic.videoId}
                            onClick={() => {
                              if (topic.bookId) {
                                navigate(`/courses/${encryptId(topic.bookId)}/${encryptId(topic.videoId)}`);
                              } else {
                                navigate(`/watch/lecture/${encryptId(topic.videoId)}`);
                              }
                            }}
                            className="bg-slate-50/50 border border-slate-100 rounded-2xl p-4 flex items-center justify-between gap-4 hover:border-purple-200 hover:bg-purple-50/10 hover:shadow-md hover:shadow-purple-500/5 cursor-pointer transition duration-200"
                          >
                            <div className="flex items-center gap-3">
                              <span className={`w-8.5 h-8.5 rounded-xl flex items-center justify-center transition-colors duration-200 ${
                                isCompleted ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-150 text-gray-400'
                              }`}>
                                {isCompleted ? <FiCheckCircle size={16} /> : <FiPlay size={14} />}
                              </span>
                              <div>
                                <h5 className="text-xs font-bold text-gray-800 leading-snug">{topic.topicName}</h5>
                                <span className="text-[10px] text-gray-400 font-semibold">{Math.round(topic.duration / 60)} mins</span>
                              </div>
                            </div>

                            {isCompleted && (
                              <span className="text-[10px] font-extrabold uppercase bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full border border-emerald-200">
                                Done
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default ProgressPage;